import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { SettingsShell } from "@/components/SettingsShell";
import { Icon } from "@/components/Icon";

/**
 * /settings/connect — "AI tools" settings page. Explains how to connect a
 * desktop AI assistant (Claude Desktop, ChatGPT) to this workspace via the
 * built-in remote MCP server at `/mcp`. Self-contained docs; no API calls.
 */
function ConnectSettings() {
  const endpoint =
    typeof window !== "undefined" ? `${window.location.origin}/mcp` : "/mcp";
  const [copied, setCopied] = useState(false);

  async function copy() {
    try {
      await navigator.clipboard.writeText(endpoint);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // Clipboard can be blocked (insecure context / permissions). The URL is
      // visible in the box, so the user can still select + copy manually.
    }
  }

  return (
    <SettingsShell>
      {/* Intro + endpoint */}
      <div className="panel">
        <div className="panel-head">
          <h3>Connect an AI assistant</h3>
          <p>
            Link a desktop AI tool to this workspace and manage your projects,
            tasks, and comments just by chatting. You sign in with your existing
            account, and the assistant can only do what you're allowed to do.
          </p>
        </div>
        <div className="panel-body">
          <label className="cx-label" htmlFor="mcp-endpoint">
            Server URL
          </label>
          <div className="cx-endpoint">
            <code id="mcp-endpoint" className="cx-url">
              {endpoint}
            </code>
            <button
              type="button"
              className="btn btn-secondary btn-sm"
              onClick={copy}
            >
              <Icon name="clip" />
              {copied ? "Copied" : "Copy"}
            </button>
          </div>
          <p className="t-sm t-muted cx-hint">
            Paste this URL into your AI tool's connector settings. Destructive
            actions (like deleting a project) ask the assistant to confirm with
            you first.
          </p>
        </div>
      </div>

      {/* Claude Desktop */}
      <div className="panel">
        <div className="panel-head">
          <h3>Claude Desktop</h3>
          <p>Anthropic's desktop app.</p>
        </div>
        <div className="panel-body">
          <ol className="cx-steps">
            <li>
              Open <strong>Settings → Connectors</strong>.
            </li>
            <li>
              Click <strong>Add custom connector</strong>.
            </li>
            <li>
              Give it a name (e.g. <em>Project Manager</em>) and paste the{" "}
              <strong>Server URL</strong> above.
            </li>
            <li>
              A browser window opens — <strong>sign in</strong> with your email
              code or passkey and approve access.
            </li>
            <li>
              The connector turns active. Ask Claude to “list my tasks” or
              “create a task” to try it.
            </li>
          </ol>
        </div>
      </div>

      {/* ChatGPT */}
      <div className="panel">
        <div className="panel-head">
          <h3>ChatGPT</h3>
          <p>OpenAI's desktop and web app.</p>
        </div>
        <div className="panel-body">
          <ol className="cx-steps">
            <li>
              Open <strong>Settings → Connectors</strong> (look for{" "}
              <em>Advanced</em> or <em>custom connector</em>).
            </li>
            <li>
              Add a connector and paste the <strong>Server URL</strong> above as
              the MCP server URL.
            </li>
            <li>
              <strong>Sign in</strong> in the browser window and approve access.
            </li>
            <li>
              Enable the connector in a chat (or in Deep Research) and ask it to
              read or update your work.
            </li>
          </ol>
          <p className="t-sm t-muted cx-hint">
            Custom connector availability depends on your ChatGPT plan
            (typically Plus, Pro, Business, Enterprise, or Edu). If you don't
            see connector settings, your plan may not support them yet.
          </p>
        </div>
      </div>

      {/* What it can do */}
      <div className="panel">
        <div className="panel-head">
          <h3>What the assistant can do</h3>
          <p>The same things you can do in this app.</p>
        </div>
        <div className="panel-body">
          <ul className="cx-caps">
            <li>
              Browse and search projects, tasks, comments, files, and activity
            </li>
            <li>Create, update, move, assign, and delete tasks</li>
            <li>Create, update, archive, and delete projects</li>
            <li>Add comments and tags, and view team workload and metrics</li>
            <li>
              Attach <code>@</code>-mentionable projects and tasks as context,
              and run ready-made prompts like “weekly status” and “standup”
            </li>
          </ul>
          <p className="t-sm t-muted cx-hint">
            Everything runs as you, through the same permissions as the web app.
            You can disconnect at any time from your AI tool's connector
            settings, or sign out of the session under <strong>Security</strong>
            .
          </p>
        </div>
      </div>
    </SettingsShell>
  );
}

export const Route = createFileRoute("/settings/connect")({
  component: ConnectSettings,
});
